import { UserPlus, User } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';





import { inviteCustomerCompanyMember, getCustomerCompanyLocalManagers } from '../../../../../services/staffingService';



// Validation schema for the invitation form
const inviteSchema = yup.object().shape({
    email: yup.string()
        .email('Invalid email')
        .required('Email is required')
});

const roles = [
    { label: 'Admin', value: 'admin' },
    { label: 'Manager', value: 'manager' },
    { label: 'Member', value: 'member' }
];

const CustomerInvite = ({ providerCompanyId, companyData }) => {
    const [invitedMembers, setInvitedMembers] = useState([]);
    const [existingMembers, setExistingMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRole, setSelectedRole] = useState('member');
    const [inviteResponse, setInviteResponse] = useState('');
    const [inviteError, setInviteError] = useState(false);

    const { register, handleSubmit, formState: { errors }, reset } = useForm({
        resolver: yupResolver(inviteSchema),
    });

    // Fetch existing members when component mounts or companyData changes
    useEffect(() => {
        const fetchExistingMembers = async () => {
            if (companyData?._id && providerCompanyId) {
                try {
                    setLoading(true);
                    const response = await getCustomerCompanyLocalManagers(providerCompanyId, companyData._id);
                    setExistingMembers(response.data.managers || response || []); // Handle different response structures
                } catch (error) {
                    console.error('Failed to fetch existing members:', error);
                    setExistingMembers([]);
                } finally {
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        };

        fetchExistingMembers();
    }, [providerCompanyId, companyData?._id]);

    const onSubmit = async (data) => {
        try {
            let customerCompanyLocalId = companyData._id ; 
            let customerCompanyGlobalId = companyData.customer_company_id ;
            let dt = { email : data.email , role : selectedRole }

            const resp = await inviteCustomerCompanyMember(providerCompanyId, customerCompanyLocalId, customerCompanyGlobalId , dt)

            setInvitedMembers([...invitedMembers, {
                email: data.email,
                role: selectedRole,
                date: new Date().toLocaleDateString()
            }]);
            
            setInviteResponse('Invitation sent successfully');
            setInviteError(false);
            reset();
        } catch (error) {
            console.error('Failed to send invitation:', error);
            setInviteResponse('Failed to send invitation');
            setInviteError(true);
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                    <UserPlus className="text-gray-500" style={{ fontSize: '18px' }} />
                    <h3 className="text-xs font-normal text-gray-600">Invite team member to customer portal</h3>
                </div>
                
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="md:col-span-2">
                            <label className="form-label text-xs">Email Address</label>
                            <input
                                type="email"
                                className="form-input w-full"
                                {...register('email')}
                                placeholder="Enter member email address"
                            />
                        </div>
                        <div>
                            <label className="form-label text-xs">Role</label>
                            <select
                                value={selectedRole}
                                onChange={(e) => setSelectedRole(e.target.value)}
                                className="form-select w-full"
                            >
                                {roles.map((role) => (
                                    <option key={role.value} value={role.value}>
                                        {role.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            {errors.email && (
                                <div className="form-error text-xs">{errors.email.message}</div>
                            )}
                            {inviteResponse && (
                                <div className={`text-xs ${inviteError ? 'text-[var(--destructive)]' : 'text-green-600'}`}>
                                    {inviteResponse}
                                </div>
                            )}
                        </div>
                        <button 
                            type="submit" 
                            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity ml-3"
                        >
                            <UserPlus style={{ fontSize: '16px' }} />
                            Send Invite
                        </button>
                    </div>
                </form>
            </div>

            {/* Existing Members Section */}
            {!loading && existingMembers && existingMembers.length > 0 && (
                <div className="space-y-3">
                    <h4 className="text-sm font-medium text-[var(--foreground)] mb-4">Team Members</h4>
                    <div className="space-y-2">
                        {existingMembers.map((member, index) => {
                            const fullName = [member.firstName, member.lastName].filter(Boolean).join(' ');
                            const displayName = fullName || member.name || 'Unknown User';
                            const email = member.email || member.userEmail;
                            
                            return (
                                <div key={member.id || index} className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                                    <div className="w-8 h-8 bg-[var(--primary)] text-white rounded-full flex items-center justify-center text-sm font-medium">
                                        {displayName.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-[var(--foreground)] text-sm truncate">
                                            {displayName}
                                        </div>
                                        {email && (
                                            <div className="text-xs text-[var(--muted-foreground)] truncate">
                                                {email}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Recently Invited Members Section */}
            {invitedMembers.length > 0 && (
                <div className="space-y-3">
                    <h4 className="text-sm font-medium text-[var(--foreground)] mb-4">Recently Invited</h4>
                    <div className="space-y-2">
                        {invitedMembers.map((member, index) => (
                            <div key={index} className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                                <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                                    {member.email.charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-[var(--foreground)] text-sm truncate">
                                        {member.email}
                                    </div>
                                    <div className="text-xs text-[var(--muted-foreground)]">
                                        Invited {member.date}
                                    </div>
                                </div>
                                <div className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                                    Pending
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="flex items-center justify-center py-8">
                    <div className="text-sm text-[var(--muted-foreground)]">Loading team members...</div>
                </div>
            )}

            {/* Empty State */}
            {!loading && existingMembers.length === 0 && invitedMembers.length === 0 && (
                <div className="text-center py-8">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <User className="text-gray-400" style={{ fontSize: '24px' }} />
                    </div>
                    <div className="text-sm text-[var(--muted-foreground)] mb-2">No team members yet</div>
                    <div className="text-xs text-[var(--muted-foreground)]">Send your first invitation to get started</div>
                </div>
            )}
        </div>
    );
};

export default CustomerInvite;
